import { generateId } from '@appblocks/utils';
import {
    Setting,
    SettingType,
    CodeBlock,
    FeatureType,
    Define,
    ValidationMessage,
    SettingOption,
    SettingState,
    FlowEdge,
    FlowVariable,
    TibboDataType,
} from './types';


export default class BaseFeature {
    id: string;

    name: string;

    icon?: string;

    files: Array<string>;

    settings: Array<Setting>;

    fileLinks: Array<string>;

    codeBlocks: Array<CodeBlock>;

    singleInstance: boolean;

    featureType: FeatureType;

    details: string;

    description: string;

    priority: number = 0;

    project: any;

    defines: Array<Define>;

    linkedFeature: string;

    capabilities?: Array<string>;

    /**
     * This specifies the requirements that need to be met for the feature to be available.
     * If left blank then the feature is always available.
     * If an item is an array then at least one requirement in the array must be met.
     * If an item is a string then it is a hard requirement.
     *
     * For peripherals on outer level (e.g. SPI, I2C, GPIO) the requirement needs to contain 'board'
     * For peripherals on a bus (e.g. SPI device, I2C device) the requirement needs to contain
     * 'spi' or 'i2c' (depending on the bus)
     * An example is ST7789 which requires 'spi' and 'board' and thus can be added on the
     * outer level and also on a SPI bus.
     */
    requirements?: Array<string | string[]>;

    /** This specifies devices that it is available from.
     * If left blank then all device types are supported */
    devices: string[];

    /** This specifies devices that it is NOT available from.
     * If left blank then it reads platforms property */
    unsupportedDevices: string[];

    constructor() {
        this.files = [];
        this.settings = [{
            id: 'ID',
            name: 'ID',
            settingType: SettingType.HIDDEN,
            required: true,
            default: generateId(),
        }];
        this.description = '';
        this.fileLinks = [];
        this.codeBlocks = [];
        this.singleInstance = true;
        this.devices = [];
        this.unsupportedDevices = [];
        this.featureType = FeatureType.FEATURE;
    }

    initSettings(project?: any, parentFeature?: any, defaultValue?: string) {
        if (project !== undefined) {
            this.project = project;
        }
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].value === undefined) {
                if (project !== undefined
                    && this.settings[i].id === defaultValue) {
                    this.settings[i].value = project
                        .getDefaultValue(this.id, this.settings[i].default, this.settings[i].id);
                } else {
                    this.settings[i].value = this.settings[i].default;
                }
            }
            if (this.settings[i].settingType === SettingType.ARRAY) {
                if (this.settings[i].value.length > 0) {
                    for (let j = 0; j < this.settings[i].value.length; j += 1) {
                        this.settings[i].value[j].initSettings(project);
                    }
                }
            }
        }
    }

    getState(settingId: string): SettingState {
        return SettingState.ENABLED;
    }

    getSetting(settingId: string) {
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === settingId) {
                return this.settings[i];
            }
        }
        return undefined;
    }

    getID(): string {
        return this.getSettingValue('ID');
    }

    /**
     * Gets value for a particular setting, can specify whether to return
     * the stored value or the value stored in connected setting.
     * The returned value contains quotes if dataType property of setting
     * is set to TibboDataType.STRING
     * @param settingId ID of setting
     * @param getStoredValue (optional) whether to fetch stored value disregarding connected setting
     * @returns string or array The value of the setting
     */
    getSettingValue(settingId: string, getStoredValue?: boolean) {
        let m;
        let regex;
        const project = this.project;
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === settingId) {
                let result = this.settings[i].value;
                if ((this.settings[i].dataType !== undefined || this.settings[i].linkToSetting)
                    && !getStoredValue) {
                    result = this.settings[i].value.toString();
                    const featureId = this.getID();
                    for (let j = 0; j < this.project.features.length; j += 1) {
                        const feature = this.project.features[j];
                        if (feature.id === 'linked_setting') {
                            if (feature.getSettingValue('FEATURE') === featureId
                                && feature.getSettingValue('SETTINGID') === settingId) {
                                const settingFeature = project.getFeature(feature.getSettingValue('LINKEDSETTING'));
                                if (settingFeature === undefined) {
                                    break;
                                }
                                const settingId = settingFeature.getSettingValue('SETTINGID');
                                result = project.convertDataType(
                                    `${settingId}`,
                                    TibboDataType.STRING,
                                    this.settings[i].dataType,
                                );
                                return result;
                            }
                        }
                    }
                    // if (result.indexOf('$') >= 0) {
                    //     regex = /\$\$([A-Za-z0-9_]+)/gm;
                    //     m = regex.exec(result);
                    //     while (m !== null) {
                    //         if (m.index === regex.lastIndex) {
                    //             regex.lastIndex += 1;
                    //         }
                    //         for (let j = 0; j < m.length; j += 1) {
                    //             const match = m[j];
                    //             result = result.replace(
                    // `$$${match}`, `"+stg_get("${match}",0)+"`);
                    //         }

                    //         m = regex.exec(result);
                    //     }
                    //     regex = /\$([A-Za-z0-9_]+(\([0-9]\))?)/gm;
                    //     m = regex.exec(result);
                    //     while (m !== null) {
                    //         if (m.index === regex.lastIndex) {
                    //             regex.lastIndex += 1;
                    //         }
                    //         for (let j = 1; j < m.length; j += 1) {
                    //             const match = m[j];
                    //             result = result.replace(`$${match}`, `"+${match}+"`);
                    //         }

                    //         m = regex.exec(result);
                    //     }
                    // }
                    if (this.settings[i].dataType === TibboDataType.SETTING) {
                        if (this.settings[i].setting?.DATATYPE === 'S') {
                            return `"${result}"`;
                        }
                    }
                    return result;
                }

                return result;
            }
        }
        return undefined;
    }

    getSettingName(settingId: string) {
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === settingId) {
                return this.settings[i].name;
            }
        }
        return '';
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const setting = this.getSetting(settingId);
        if (setting === undefined) {
            return [];
        }
        const referencedFeatureId = setting.refId;
        if (referencedFeatureId !== '' && referencedFeatureId !== undefined) {
            const options = [];
            const items = this.project.getFeatureArray(referencedFeatureId);
            options.push({
                name: 'Undefined',
                value: '',
            });
            for (let j = 0; j < items.length; j++) {
                const item = items[j];
                const itemName = item.getName();
                options.push({
                    name: itemName,
                    value: item.getID(),
                });
            }
            return options;
        }
        if (setting.capabilityRequirement) {
            const options = [];
            options.push({
                name: 'Undefined',
                value: '',
            });
            for (let j = 0; j < this.project.features.length; j++) {
                const feature = this.project.features[j];
                if (feature.capabilities
                    && feature.capabilities.indexOf(setting.capabilityRequirement) !== -1) {
                    options.push({
                        name: feature.getName(),
                        value: feature.getID(),
                    });
                }
            }
            return options;
        }
        return setting.options;
    }

    getSettingValueOptionName(settingId: string) {
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === settingId) {
                const options = this.getSettingOptions(settingId);
                if (options === undefined) {
                    continue;
                }
                for (let j = 0; j < options.length; j += 1) {
                    if (options[j].value === this.settings[i].value) {
                        return options[j].name;
                    }
                }
            }
        }
        return undefined;
    }

    setSettingValue(settingId: string, settingValue: any) {
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === settingId) {
                if (this.settings[i].refId !== undefined) {
                    // array
                    // for (let j = 0; j < settingValue.length; j++) {
                    //     const setting = this.getSetting(this.settings[i].refId);
                    //     if (setting !== undefined) {
                    //         setting.value = settingValue[j];
                    //     }
                    // }
                }
                this.settings[i].value = settingValue;
                return;
            }
        }
    }

    getDependencies(): Array<string> {
        return [];
    }

    validate(): Array<ValidationMessage> {
        return [];
    }

    getCodeBlocks(): any {
        return this.codeBlocks;
    }

    getName() {
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === 'NAME') {
                return this.settings[i].value;
            }
        }
        return this.id;
    }

    getFiles(): Array<string> {
        return this.files;
    }

    getFileLinks(): Array<string> {
        return this.fileLinks;
    }

    /**
     * Getting the set of defines required for the current configuration
     * @returns An array of Define objects
     */
    getDefines(): Array<Define> {
        if (this.defines !== undefined) {
            return this.defines;
        }
        return [];
    }

    /**
     * Get documentation URL or markdown to show as tooltip
     * @returns URL or markdown to show
     */
    getDetails(): string {
        return '';
    }

    getDefault(settingId: string): string {
        const setting = this.getSetting(settingId);
        if (setting !== undefined) {
            return setting.default;
        }
        return '';
    }

    getEnabled(): boolean {
        const enabledValue = this.getSettingValue('ENABLED');
        if (enabledValue === undefined) {
            return true;
        }
        return enabledValue === '1';
    }

    getDocURL(): string {
        return `${process.env.HOME_URL}/docs/features/${this.id}`;
    }
}
